import React, { memo, useCallback, useId, useMemo, useState } from 'react';
import './StarRating.css';

export interface StarRatingProps {
  /** Valor atual (1..5). Use 0 para "sem nota". */
  value: number;
  /** Callback chamado quando o usuario seleciona uma nota. */
  onChange: (rating: number) => void;
  /** Tamanho visual das estrelas. Padrao: 'md'. */
  size?: 'sm' | 'md' | 'lg';
  /** Desabilita interacao (modo somente leitura). */
  readOnly?: boolean;
  /** Quantidade maxima de estrelas. Padrao: 5. */
  max?: number;
  /** Rotulo acessivel para o grupo. */
  ariaLabel?: string;
  /** Identificador opcional usado para nomear os inputs. */
  name?: string;
  /** Classe extra no container. */
  className?: string;
}

const RATING_LABELS: Record<number, string> = {
  1: 'Muito ruim',
  2: 'Ruim',
  3: 'Regular',
  4: 'Bom',
  5: 'Excelente',
};

/**
 * Componente reutilizavel de avaliacao por estrelas.
 *
 * - Acessivel (role="radiogroup" + role="radio" por estrela, suporte a teclado).
 * - Pure (memo) com handlers via useCallback para evitar renders desnecessarios.
 * - Sem dependencias externas; usa bootstrap-icons que ja esta no projeto.
 * - Responsivo via tamanho fluido em CSS (clamp / variantes sm/md/lg).
 */
const StarRating: React.FC<StarRatingProps> = memo(({
  value,
  onChange,
  size = 'md',
  readOnly = false,
  max = 5,
  ariaLabel = 'Avaliação',
  name,
  className = '',
}) => {
  const reactId = useId();
  const groupName = name ?? `star-rating-${reactId}`;
  const [hoverValue, setHoverValue] = useState<number>(0);

  const stars = useMemo(
    () => Array.from({ length: max }, (_, i) => i + 1),
    [max],
  );

  const handleSelect = useCallback(
    (rating: number) => {
      if (readOnly) return;
      onChange(rating);
    },
    [onChange, readOnly],
  );

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLButtonElement>, rating: number) => {
      if (readOnly) return;
      switch (event.key) {
        case 'ArrowRight':
        case 'ArrowUp': {
          event.preventDefault();
          const next = Math.min(max, rating + 1);
          onChange(next);
          break;
        }
        case 'ArrowLeft':
        case 'ArrowDown': {
          event.preventDefault();
          const prev = Math.max(1, rating - 1);
          onChange(prev);
          break;
        }
        case 'Home': {
          event.preventDefault();
          onChange(1);
          break;
        }
        case 'End': {
          event.preventDefault();
          onChange(max);
          break;
        }
        case ' ':
        case 'Enter': {
          event.preventDefault();
          onChange(rating);
          break;
        }
        default:
          break;
      }
    },
    [max, onChange, readOnly],
  );

  const effectiveValue = hoverValue || value;
  const ratingLabel = effectiveValue > 0 ? RATING_LABELS[effectiveValue] ?? '' : '';

  return (
    <div
      role="radiogroup"
      aria-label={ariaLabel}
      className={`feedback-star-rating size-${size} ${readOnly ? 'is-readonly' : ''} ${className}`}
      onMouseLeave={() => !readOnly && setHoverValue(0)}
    >
      <div className="feedback-star-rating__stars">
        {stars.map((rating) => {
          const isFilled = rating <= effectiveValue;
          const label = `${rating} ${rating === 1 ? 'estrela' : 'estrelas'} - ${RATING_LABELS[rating] ?? ''}`;
          return (
            <button
              key={rating}
              type="button"
              role="radio"
              aria-checked={value === rating}
              aria-label={label}
              tabIndex={readOnly ? -1 : value === rating || (value === 0 && rating === 1) ? 0 : -1}
              className={`feedback-star-rating__star ${isFilled ? 'is-filled' : ''}`}
              onClick={() => handleSelect(rating)}
              onMouseEnter={() => !readOnly && setHoverValue(rating)}
              onFocus={() => !readOnly && setHoverValue(rating)}
              onBlur={() => !readOnly && setHoverValue(0)}
              onKeyDown={(e) => handleKeyDown(e, rating)}
              disabled={readOnly}
              data-name={groupName}
            >
              <i className={`bi ${isFilled ? 'bi-star-fill' : 'bi-star'}`} aria-hidden="true"></i>
            </button>
          );
        })}
      </div>
      {!readOnly && (
        <span
          className="feedback-star-rating__label"
          aria-live="polite"
          aria-atomic="true"
        >
          {ratingLabel}
        </span>
      )}
    </div>
  );
});

StarRating.displayName = 'StarRating';

export default StarRating;
